import { useEffect, useRef } from 'react';
import { trackEvent } from '@/utils/gaDataPush';
import { trackEventDirect } from '@/widgets/navLinks/ga';

type NavLinksBehaviorOptions = {
  rootId: string;
  /** Only bind when the nav is rendered as static HTML; the React tree binds its own handlers. */
  enabled: boolean;
  currencyCode?: string;
  isGlobalEFlag?: boolean;
  isGlobalESite?: boolean;
  onPromoTermsClick?: () => void;
};

const OPEN_CLASS = 'open';
const LIST_SELECTOR = 'ul.king-navgation';
const ITEM_SELECTOR = 'ul.king-navgation > li';

function positionCategoryBg(item: HTMLElement) {
  const categoryBg = item.querySelector('.categorybg');
  if (!categoryBg) {
    return;
  }

  const left = categoryBg.getBoundingClientRect().left + 10;
  const bgColor = item.querySelector<HTMLElement>('.categorybgcolor');
  if (bgColor) {
    bgColor.style.left = `${left}px`;
  }

  const quiz = document.querySelector<HTMLElement>('.diamond-quiz-top');
  if (!quiz) {
    return;
  }

  const quizLeft = quiz.getBoundingClientRect().left;
  const parentWidth = quiz.parentElement?.offsetWidth ?? 0;
  quiz.style.marginRight = `${parentWidth - (left - quizLeft - 15)}px`;
}

function pushNavGaClick(link: HTMLAnchorElement, url: string, label: string) {
  const w = window as any;
  if (typeof w.homePageDataPush === 'function') {
    w.homePageDataPush('top_navigation_click', url, label, 'top nav');
  }
  // Match utils/gaDataPush.handleGaClick: data-event* attrs still drive GA Universal.
  trackEvent({ currentTarget: link } as Parameters<typeof trackEvent>[0]);
}

/**
 * Restores the interactivity that `NavLink`/`NavLinkDropdown` normally provide through React
 * props. The nav is injected as static HTML so Dynamic Yield can rewrite it before hydration
 * without React reconciling it back, which means hover, GA and the promo modal have to be
 * delegated from the root element instead.
 */
export default function useNavLinksBehavior({
  rootId,
  enabled,
  currencyCode,
  isGlobalEFlag,
  isGlobalESite,
  onPromoTermsClick,
}: NavLinksBehaviorOptions) {
  const onPromoTermsClickRef = useRef(onPromoTermsClick);
  onPromoTermsClickRef.current = onPromoTermsClick;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const root = document.getElementById(rootId);
    const list = root?.querySelector<HTMLElement>(LIST_SELECTOR);
    if (!root || !list) {
      return undefined;
    }

    const closeAll = () => {
      list.querySelectorAll<HTMLElement>(`:scope > li.${OPEN_CLASS}`).forEach(item => {
        item.classList.remove(OPEN_CLASS);
      });
    };

    const openTerms = (event: Event) => {
      event.preventDefault();
      closeAll();
      onPromoTermsClickRef.current?.();
    };

    const handleMouseOver = (event: Event) => {
      const item = (event.target as HTMLElement)?.closest<HTMLElement>(ITEM_SELECTOR);
      if (!item || item.classList.contains(OPEN_CLASS)) {
        return;
      }
      closeAll();
      item.classList.add(OPEN_CLASS);
      positionCategoryBg(item);
    };

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (target?.closest('.nav-promo__terms-apply')) {
        openTerms(event);
        return;
      }

      const link = target?.closest<HTMLAnchorElement>('a');
      if (!link || !root.contains(link)) {
        return;
      }

      // Promo tile and diamond quiz links report straight from their data attributes.
      if (link.closest('.recomment') || link.closest('.diamond-quiz-top')) {
        const { category, action, label } = link.dataset;
        if (category && action && label) {
          trackEventDirect(category, action, label);
        }
        return;
      }

      const label = (link.textContent || '').trim();
      pushNavGaClick(link, link.getAttribute('href') || '', label);

      if (link.classList.contains('nav-menu-item')) {
        trackEventDirect('Top Navigation', 'Click', label);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') {
        return;
      }
      if ((event.target as HTMLElement)?.closest('.nav-promo__terms-apply')) {
        openTerms(event);
      }
    };

    list.addEventListener('mouseover', handleMouseOver);
    list.addEventListener('mouseleave', closeAll);
    root.addEventListener('click', handleClick);
    root.addEventListener('keydown', handleKeyDown);

    return () => {
      list.removeEventListener('mouseover', handleMouseOver);
      list.removeEventListener('mouseleave', closeAll);
      root.removeEventListener('click', handleClick);
      root.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, rootId]);

  // The island is rendered with '$' because the server cannot know the GlobalE selection.
  useEffect(() => {
    if (!enabled || !currencyCode || currencyCode === '$') {
      return;
    }

    const shouldReplace = (isGlobalEFlag && !isGlobalESite) || !isGlobalEFlag;
    if (!shouldReplace) {
      return;
    }

    const root = document.getElementById(rootId);
    root?.querySelectorAll('.subnav-content .nav li a').forEach(link => {
      link.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE && node.nodeValue?.includes('$')) {
          node.nodeValue = node.nodeValue.replace(/\$/g, currencyCode);
        }
      });
    });
  }, [enabled, rootId, currencyCode, isGlobalEFlag, isGlobalESite]);
}
